/**
 * Navigation blocker for React pages. Pages with unsaved changes register a getState
 * callback; before navigating away, the host (PageRouter, Sidebar, Topbar) calls
 * checkNavigationBlockers() and waits for it to resolve before proceeding.
 *
 * HOST INTEGRATION (2 steps):
 * 1. Before navigation: await checkNavigationBlockers() (or .then(...)), then proceed.
 * 2. When rendering the page component (or wiring up an MFE parcel): call
 *    registerNavigationBlocker in a useEffect, return the unregister function as the
 *    cleanup. MFEs receive registerNavigationBlocker as a prop from the host.
 *
 * Feature-flagged: isUnifiedBlockerEnabled() gates the new merged registry. When the
 * flag is OFF, host call sites use the legacy parcel API exclusively (see "LEGACY
 * PARCEL API" section below). The legacy block is removed once the flag is fully on.
 */

export type UnsavedChangesModalChoice = 'save' | 'ignore';

export interface NavigationBlockerState {
  hasUnsavedChanges: boolean;
  save?: () => Promise<void> | void;
  showModal?: () => Promise<UnsavedChangesModalChoice>;
  onSaveError?: (error: unknown) => void;
}

export type NavigationBlockerGetState = () => NavigationBlockerState;

type Unregister = () => void;

const MESSAGE = 'This page contains unsaved changes. Save changes?';

function makeUnregister(list: NavigationBlockerGetState[], getState: NavigationBlockerGetState) {
  return function unregister() {
    const i = list.indexOf(getState);
    if (i >= 0) list.splice(i, 1);
  };
}

async function runOne(state: NavigationBlockerState): Promise<void> {
  const runSave = async (): Promise<void> => {
    if (typeof state.save !== 'function') return;
    try {
      await Promise.resolve(state.save());
    } catch (error) {
      if (typeof state.onSaveError === 'function') {
        state.onSaveError(error);
      }
      throw error;
    }
  };
  if (typeof state.showModal === 'function') {
    const choice = await state.showModal();
    if (choice === 'save') {
      await runSave();
    }
    return;
  }
  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(MESSAGE);
  if (!confirmed) {
    return;
  }
  await runSave();
}

function runChecks(list: NavigationBlockerGetState[]): Promise<void> {
  const states = list.map((getState) => getState());
  const withUnsaved = states.filter((state) => state.hasUnsavedChanges);

  if (withUnsaved.length === 0) {
    return Promise.resolve();
  }

  return withUnsaved.reduce(
    (promise, state) => promise.then(() => runOne(state)),
    Promise.resolve() as Promise<void>
  );
}

// =============================================================================
// Unified registry (new code path, gated on `ui.navigation_blocker.unified`)
// =============================================================================

const blockers: NavigationBlockerGetState[] = [];

/**
 * Register a navigation blocker. Call this on component mount; call the returned function
 * in the useEffect cleanup on unmount.
 *
 * @param getState - Function returning current { hasUnsavedChanges, save? }. Called when
 *   the user attempts to navigate away.
 * @returns Unregister function. Call on component unmount.
 */
export function registerNavigationBlocker(getState: NavigationBlockerGetState): Unregister {
  blockers.push(getState);
  return makeUnregister(blockers, getState);
}

/**
 * Check all registered blockers. If any report unsaved changes, prompt the user:
 * - If the blocker provides showModal(), use it; on 'save' call save().
 * - Otherwise use window.confirm(); on OK call save().
 * Resolves so the host can proceed with navigation, or rejects when save() fails
 * (navigation does not proceed on rejection).
 */
export function checkNavigationBlockers(): Promise<void> {
  return runChecks(blockers);
}

export const navigationBlocker = {
  register: registerNavigationBlocker,
  check: checkNavigationBlockers,
};

/**
 * True when the merged-registry code path should be used. When false, host call sites
 * fall back to the legacy parcel API below.
 */
export function isUnifiedBlockerEnabled(): boolean {
  return window.lsCloud.Modules.hasModule('ui.navigation_blocker.unified');
}

// =============================================================================
// LEGACY PARCEL API (active when `ui.navigation_blocker.unified` is OFF).
// Remove this entire block + the FF helper once the flag is fully rolled out.
// =============================================================================

const parcelBlockers: NavigationBlockerGetState[] = [];

/**
 * Paths where the legacy parcel navigation blocker is implemented. The host only runs
 * checkParcelNavigationBlockers when the current React route is one of these (or a sub-path).
 */
export const PARCEL_NAVIGATION_BLOCKER_PATHS: readonly string[] = [
  '/settings/invoicing/credit-accounts',
];

export function isPathWithParcelNavigationBlocker(pathname: string): boolean {
  return PARCEL_NAVIGATION_BLOCKER_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

/**
 * Legacy: register a navigation blocker into the parcel-only registry. Used only when
 * the unified-blocker FF is off. The QM credit-accounts parcel receives this as a prop.
 */
export function registerParcelNavigationBlocker(getState: NavigationBlockerGetState): Unregister {
  parcelBlockers.push(getState);
  return makeUnregister(parcelBlockers, getState);
}

/**
 * Legacy: check the parcel registry. Used only when the unified-blocker FF is off.
 */
export function checkParcelNavigationBlockers(): Promise<void> {
  return runChecks(parcelBlockers);
}
